import { useCallback, useEffect, useState } from 'react';
import { Loader2, Coffee, AlertTriangle, Radio, FileText, Shield } from 'lucide-react';
import { API_BASE_URL, cn, formatTimestamp } from '../../lib/utils';
import { useAppSelector } from '../../store';
import type { Resource } from '../../types';

interface TimelineEntry {
  kind: 'audit' | 'dispatch' | 'incident' | 'incident_log' | 'break';
  timestamp: string;
  title: string;
  details?: string;
  userName?: string;
  incidentId?: string;
  isCurrent?: boolean;
}

interface IncidentSummary {
  id: string;
  title: string;
  status: string;
  isCurrent: boolean;
}

const kindIcon: Record<TimelineEntry['kind'], React.ElementType> = {
  audit: Shield,
  dispatch: Radio,
  incident: AlertTriangle,
  incident_log: FileText,
  break: Coffee,
};

const kindColor: Record<TimelineEntry['kind'], string> = {
  audit: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  dispatch: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
  incident: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  incident_log: 'text-muted-foreground bg-accent border-border',
  break: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
};

interface ResourceAuditLogPanelProps {
  resource: Resource;
  refreshKey?: number;
  fullHeight?: boolean;
}

export default function ResourceAuditLogPanel({ resource, refreshKey = 0, fullHeight = false }: ResourceAuditLogPanelProps) {
  const { token } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [incidents, setIncidents] = useState<IncidentSummary[]>([]);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    if (!resource || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/resources/${resource.id}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setTimeline(data.timeline || []);
      setIncidents(data.incidents || []);
    } finally {
      setLoading(false);
    }
  }, [resource, token]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshKey]);

  useEffect(() => {
    setSelectedIncidentId(null);
  }, [resource.id, refreshKey]);

  const filteredTimeline = selectedIncidentId
    ? timeline.filter((entry) => entry.incidentId === selectedIncidentId)
    : timeline;

  return (
    <div className={cn('space-y-3', fullHeight && 'h-full flex flex-col min-h-[320px]')}>
      <div className="flex items-center justify-between flex-shrink-0">
        {!fullHeight && (
          <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Audit Log</h3>
        )}
        {!loading && (
          <span className={cn('text-[10px] text-muted-foreground', fullHeight && 'ml-auto')}>
            {filteredTimeline.length}
            {selectedIncidentId ? ` of ${timeline.length}` : ''} entries
          </span>
        )}
      </div>

      {incidents.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground mb-1.5">Related incidents</p>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setSelectedIncidentId(null)}
              className={cn(
                'text-[10px] px-2 py-1 rounded-lg border transition-colors',
                selectedIncidentId === null
                  ? 'border-primary/40 bg-primary/10 text-primary font-semibold'
                  : 'border-border bg-background text-muted-foreground hover:border-primary/30'
              )}
            >
              All
            </button>
            {incidents.map((inc) => (
              <button
                key={inc.id}
                type="button"
                onClick={() => setSelectedIncidentId((current) => (current === inc.id ? null : inc.id))}
                className={cn(
                  'text-[10px] px-2 py-1 rounded-lg border transition-colors text-left',
                  selectedIncidentId === inc.id
                    ? 'border-primary/40 bg-primary/10 text-primary font-semibold'
                    : inc.isCurrent
                      ? 'border-primary/20 bg-primary/5 text-primary/80 hover:border-primary/30'
                      : 'border-border bg-background text-muted-foreground hover:border-primary/30'
                )}
              >
                {inc.id} · {inc.title.slice(0, 20)}{inc.title.length > 20 ? '…' : ''}
                {inc.isCurrent && ' (current)'}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={cn(
        'overflow-y-auto rounded-lg border border-border bg-background/50 p-2 space-y-1.5',
        fullHeight ? 'flex-1 min-h-0' : 'max-h-64'
      )}>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground text-xs gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading history...
          </div>
        ) : filteredTimeline.length === 0 ? (
          <p className="text-center text-xs text-muted-foreground py-8">
            {selectedIncidentId ? 'No entries for this incident.' : 'No history recorded yet.'}
          </p>
        ) : (
          filteredTimeline.map((entry, idx) => {
            const Icon = kindIcon[entry.kind];
            return (
              <div
                key={`${entry.kind}-${entry.timestamp}-${idx}`}
                className={cn('flex gap-2.5 p-2.5 rounded-lg border', kindColor[entry.kind])}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 opacity-80" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-[11px] font-semibold text-foreground">{entry.title}</p>
                    <span className="text-[9px] text-muted-foreground whitespace-nowrap">
                      {formatTimestamp(entry.timestamp)}
                    </span>
                  </div>
                  {entry.details && (
                    <p className="text-[10px] text-foreground/80 mt-0.5 leading-relaxed">{entry.details}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-1 text-[9px] text-muted-foreground">
                    {entry.userName && <span>{entry.userName}</span>}
                    {entry.incidentId && (
                      <span className="text-primary font-mono">{entry.incidentId}</span>
                    )}
                    {entry.isCurrent && <span className="text-primary font-semibold">Current assignment</span>}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
